import React, { useMemo } from 'react';
import {
  Card,
  CardActions,
  CardContent,
  CardHeader,
  Theme,
  Typography,
  useTheme,
} from '@mui/material';
import { AppRow } from '../../layout/appRow';
import { Activity } from '../../models/activity';
import { format, isFuture, isToday, isWeekend } from 'date-fns';
import { getTotalHours } from '../../services/activityService';
import { AppButton } from '../app/button/appButton';
import { TaskAlt } from '@mui/icons-material';
import { ColorPalette, TypographyFamily } from '../../styles/constants';
import { Holiday } from '../../models/holidays';
import BeachAccessOutlinedIcon from '@mui/icons-material/BeachAccessOutlined';
import Box from '@mui/material/Box';
import { AppTooltipContainer } from '../app/appTooltip';
import { convertMinutesToHours } from '../../services/conversionService';
import { generateProjectLabel } from '../../services/projectService';

const makeStyle = (theme: Theme) => ({
  card: {
    width: '440px',
    m: 2,
    mb: 0,
    pb: 0,
    display: 'flex',
    flexDirection: 'column',
    [theme.breakpoints.down('sm')]: {
      width: '300px',
    },
  },

  cardHeader: {
    background: '#E9EAEA',
    boxShadow: '0px 0px 8px rgba(0, 0, 0, 0.05);',
    [theme.breakpoints.down('sm')]: {
      p: '10px',
    },
  },

  cardHeader2: {
    background: ColorPalette.DAY_OFF_BG_COLOR,
    boxShadow: '0px 0px 8px rgba(0, 0, 0, 0.05);',
    [theme.breakpoints.down('sm')]: {
      p: '10px',
    },
  },

  appRow: {
    mb: 2,
  },

  cardActions: {
    p: 0,
    mt: 'auto',
  },

  cardContent: {
    height: '140px',
  },
  holidayName: {
    fontStyle: 'italic',
    color: ColorPalette.DARK_GRAY,
  },
  umbrellaIcon: {
    fontSize: '1.5rem',
    color: ColorPalette.DARK_GRAY,
  },
  headerDataBox: { display: 'flex', alignItems: 'flex-end' },
  holidayTitleBox: {
    display: 'flex',
    height: '100%',
    alignItems: 'center',
    justifyContent: 'center',
  },
  tooltipBox: { width: '24px', height: '24px' },
  undertimeHighlight: { border: `1px solid ${ColorPalette.WARNING_RED}` },
});

interface Props {
  activities: Activity[];
  dayWeek: string;
  onSelectDay(activityDate: string): void;
  dayOff?: Holiday;
  targetHoursToWork: number;
  showOnlyloggedTime?: boolean;
}

export function ActivityWeekTask({
  activities,
  dayWeek,
  onSelectDay,
  dayOff,
  targetHoursToWork,
  showOnlyloggedTime = false,
}: Props): JSX.Element {
  const theme = useTheme();
  const style = makeStyle(theme);

  const totalHoursDayWorked = useMemo(
    () => getTotalHours(activities),
    [activities]
  );

  const isUndertime = useMemo(
    () =>
      totalHoursDayWorked < targetHoursToWork &&
      totalHoursDayWorked !== 0 &&
      !dayOff &&
      !isWeekend(Date.parse(dayWeek)),
    [dayOff, dayWeek, targetHoursToWork, totalHoursDayWorked]
  );

  const isTimeNotLogged = useMemo(
    () =>
      totalHoursDayWorked === 0 &&
      !dayOff &&
      !isToday(Date.parse(dayWeek)) &&
      !isWeekend(Date.parse(dayWeek)) &&
      !isFuture(Date.parse(dayWeek)),
    [totalHoursDayWorked, dayOff, dayWeek]
  );

  const filteredActivities = useMemo(
    () =>
      activities.reduce((prev, current) => {
        const isSameProject = (activity: Activity) =>
          activity.task.project.id === current.task.project.id;

        if (!prev.some(isSameProject)) {
          prev.push(current);

          return prev;
        }

        return prev.map((activity) =>
          isSameProject(activity)
            ? {
                ...activity,
                hours: activity.hours + convertMinutesToHours(current.minutes),
              }
            : activity
        );
      }, [] as Activity[]),
    [activities]
  );

  const isWeekendOrFutureWithZeroHours = useMemo(
    () =>
      totalHoursDayWorked === 0 &&
      (isWeekend(Date.parse(dayWeek)) || isFuture(Date.parse(dayWeek))),

    [totalHoursDayWorked, dayWeek]
  );

  return (
    <Card
      sx={{
        ...style.card,
        ...((isUndertime || isTimeNotLogged) && style.undertimeHighlight),
      }}
    >
      <CardHeader
        sx={dayOff ? style.cardHeader2 : style.cardHeader}
        title={
          <AppRow>
            <Typography variant="h3">
              {`${format(Date.parse(dayWeek), 'iii')},
              ${format(Date.parse(dayWeek), 'd')}`}
            </Typography>
            <Box sx={style.headerDataBox}>
              {!isWeekendOrFutureWithZeroHours &&
                !showOnlyloggedTime &&
                !dayOff && (
                  <Typography variant="warningTwo" sx={{ ml: 1 }}>
                    {isWeekend(Date.parse(dayWeek)) ? (
                      `${totalHoursDayWorked}/0h`
                    ) : totalHoursDayWorked === targetHoursToWork ? (
                      <TaskAlt color="primary" />
                    ) : (
                      `${totalHoursDayWorked}/${targetHoursToWork}h`
                    )}
                  </Typography>
                )}

              {showOnlyloggedTime && totalHoursDayWorked > 0 && (
                <Typography
                  sx={{ fontFamily: TypographyFamily.NEXA }}
                >{`${totalHoursDayWorked}h`}</Typography>
              )}

              {dayOff &&
                (totalHoursDayWorked && !showOnlyloggedTime ? (
                  <Typography variant="warningTwo">
                    {`${totalHoursDayWorked}/0h`}
                  </Typography>
                ) : (
                  !totalHoursDayWorked && (
                    <AppTooltipContainer
                      content={
                        <BeachAccessOutlinedIcon sx={style.umbrellaIcon} />
                      }
                      title={dayOff.name}
                    />
                  )
                ))}
            </Box>
          </AppRow>
        }
      />
      <CardContent sx={style.cardContent}>
        {dayOff && !filteredActivities.length && (
          <Box sx={style.holidayTitleBox}>
            <Typography sx={style.holidayName}>{dayOff.name}</Typography>
          </Box>
        )}

        {filteredActivities.map((activity, index) => {
          if (index > 2) {
            return;
          }

          return (
            <AppRow key={activity.id} sx={style.appRow}>
              <Typography
                variant="body1"
                color={theme.palette.primary.contrastText}
              >
                {generateProjectLabel(
                  activity.task.project.name,
                  activity.task.project.code
                )}
              </Typography>
              <Typography variant="body1">{activity.hours}h</Typography>
            </AppRow>
          );
        })}
      </CardContent>
      <CardActions sx={style.cardActions}>
        <AppButton label="See more" onClick={() => onSelectDay(dayWeek)} />
      </CardActions>
    </Card>
  );
}
